import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PosterPlaceholder from './PosterPlaceholder';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * "What's coming", as poster rows grouped by when — not a month grid.
 *
 * The month grid at /calendar is a lookup tool: it answers "when is X out",
 * which only helps once you already have a title in mind. A landing page has to
 * work for someone who arrived with no question at all, and a grid of small
 * text chips gives them nothing to look at. Every comparable site leads with
 * artwork for that reason.
 *
 * So the same events are presented as horizontal poster rows under plain
 * time buckets. The visual language (card size, placeholder, hover) is copied
 * from UpcomingReleasesCarousel deliberately, so the page reads as one design
 * rather than two.
 */

const TYPE_LABEL = {
  // Carried where a title has no typed release event — most of the catalogue.
  RELEASE_DATE_SET: 'Release',
  STREAMING_RELEASE: 'Streaming',
  SEASON_PREMIERE: 'Premiere',
  THEATRICAL_RELEASE: 'In cinemas',
  TRAILER_RELEASED: 'Trailer',
};
const TYPE_STYLE = {
  RELEASE_DATE_SET: 'bg-slate-100 text-slate-700',
  STREAMING_RELEASE: 'bg-green-100 text-green-700',
  SEASON_PREMIERE: 'bg-blue-100 text-blue-700',
  THEATRICAL_RELEASE: 'bg-amber-100 text-amber-700',
  TRAILER_RELEASED: 'bg-rose-100 text-rose-700',
};

const todayKey = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD, local
const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString('en-CA');
};
// Parsed as a local date: new Date('2026-11-20') is UTC midnight, which is the
// 19th anywhere west of Greenwich.
const dayLabel = (d) => {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day)
    .toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
};

// Each row asks for its own window. One rolling request with a single limit
// returns the earliest rows and starves the rest: at 2,560 events a year,
// limit=200 over 120 days covered three days, so every row after "this week"
// rendered empty. The rows show 20 cards each, so 24 per window is enough.
const PER_BUCKET = 24;

const BUCKETS = [
  { key: 'today', title: 'Today', from: () => todayKey(), to: () => todayKey() },
  { key: 'week', title: 'This week', from: () => addDays(1), to: () => addDays(7) },
  { key: 'month', title: 'This month', from: () => addDays(8), to: () => addDays(31) },
  { key: 'later', title: 'Later', from: () => addDays(32), to: () => addDays(120) },
];

// Public detail route where there is one. /insider is behind ProtectedRoute, so
// linking there would put a login wall in front of a signed-out visitor's first
// click on the landing page.
const href = (e) => {
  if (e.tmdb_id) return `/${e.media_type === 'tv' ? 'tv' : 'movie'}/${e.tmdb_id}`;
  // A game has no TMDb id and never will, so it has its own public page.
  if (e.media_type === 'game') return `/game/${e.project_id}`;
  return `/insider/project/${e.project_id}`;
};

function Card({ event }) {
  return (
    <Link to={href(event)} className="flex-shrink-0 w-28 group">
      <div className="w-28 h-40 rounded-xl overflow-hidden bg-gray-100 mb-2 shadow-sm group-hover:shadow-md transition-all duration-200">
        {event.poster_url ? (
          <img
            src={event.poster_url}
            alt={event.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <PosterPlaceholder />
        )}
      </div>
      <p className="text-xs font-medium text-gray-800 line-clamp-2 leading-tight min-h-[1.875rem] mb-1">
        {event.title}
      </p>
      <div className="flex items-center gap-1 flex-wrap">
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${TYPE_STYLE[event.event_type] || 'bg-gray-100 text-gray-600'}`}>
          {TYPE_LABEL[event.event_type] || event.event_type}
        </span>
        <span className="text-[11px] text-gray-500">{dayLabel(event.event_date)}</span>
      </div>
    </Link>
  );
}

export default function UpcomingByDate() {
  const [buckets, setBuckets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const filled = await Promise.all(BUCKETS.map(async (b) => {
          const url = `${API_BASE}/api/insider/calendar`
            + `?from_date=${b.from()}&to_date=${b.to()}&limit=${PER_BUCKET}`;
          try {
            const r = await fetch(url);
            const items = r.ok ? (await r.json()).events || [] : [];
            return { ...b, items };
          } catch {
            // One empty row is better than no section at all.
            return { ...b, items: [] };
          }
        }));
        if (!cancelled) setBuckets(filled);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (isLoading) {
    return (
      <div className="bg-white border-t border-gray-100 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="h-6 w-40 bg-gray-100 rounded animate-pulse mb-4" />
          <div className="flex gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex-shrink-0 w-28 h-44 rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (!buckets.some((b) => b.items.length)) return null;

  return (
    <div className="bg-white border-t border-gray-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-baseline justify-between mb-1">
          <h2 className="text-xl font-semibold text-gray-900">What's coming</h2>
          <Link to="/calendar" className="text-sm font-medium text-videbits-600 hover:text-videbits-800">
            Full calendar →
          </Link>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          Releases, premieres and trailers across film, TV and games.
        </p>

        {buckets.filter((b) => b.items.length).map((b) => (
          <div key={b.key} className="mb-8 last:mb-0">
            <div className="flex items-baseline gap-2 mb-3">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">{b.title}</h3>
              <span className="text-xs text-gray-400">{b.items.length}</span>
            </div>
            <div
              className="flex gap-3 overflow-x-auto pb-2"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {b.items.slice(0, 20).map((e, i) => (
                <Card key={`${e.project_id}:${e.event_type}:${i}`} event={e} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
